import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { listProductConfigs, listProducts } from '../../api/products';
import './ParameterDetailsView.css';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

// Rendu dynamique (2026-09-17) : remplace les donnees 100% mock d'origine
// (parameterGroups en dur, "Corporation 58 - CHL LOTTO" fictif) par les
// vraies ProductConfig/ProductConfigPayout de la company pour cette loterie
// — meme source que ParametresLoteriePage (listProducts/listProductConfigs),
// juste en LECTURE SEULE (pas de formulaire de sauvegarde ici). Portee
// company par defaut (aucun group/bank precise sur listProductConfigs).
// `group` / `bank` (ids internes, optionnels) : parametres EFFECTIFS pour ce
// groupe / cette banque (2026-10-05). `embedded` : rendu sous le formulaire de
// SeeParametersPage (pas de Layout ni de "Back to Search", le filtre reste
// affiche) ; `searchKey` change a chaque "Search" pour recharger meme si les
// criteres n'ont pas bouge.
const ParameterDetailsView = ({
  lottery, lotteryName, onBack, group = '', bank = '', scopeLabel = '', embedded = false, searchKey = 0,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    // Valeur REELLEMENT appliquee a cette loterie (propre, sinon heritee du
    // parametre TOUS) ; loterie 0 = le parametre TOUS lui-meme (2026-09-25).
    const isAll = String(lottery) === '0';
    Promise.all([listProducts(), listProductConfigs({ tirage: lottery, effective: !isAll, group, bank })])
      .then(([products, configs]) => {
        if (cancelled) return;
        const configByProduct = Object.fromEntries(configs.map((c) => [c.product, c]));
        setRows(products.map((p) => {
          const cfg = configByProduct[p.id];
          return {
            productId: p.id,
            name: p.name,
            pointsPrice: cfg ? cfg.points_price : null,
            onlineLimit: cfg ? cfg.online_limit : null,
            localLimit: cfg ? cfg.local_limit : null,
            salesCommission: cfg ? cfg.sales_commission : null,
            savingsPct: cfg ? cfg.savings_pct : null,
            hasConfig: Boolean(cfg),
            keys: p.payout_keys.map((k) => {
              const payout = cfg?.payouts.find((pay) => pay.key === k.id);
              return { keyId: k.id, key: k.key, wonPerPoint: payout ? payout.won_per_point : null };
            }),
          };
        }));
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les parametres — verifie que le serveur backend tourne.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lottery, group, bank, searchKey]);

  const card = (
      <div className="pdv-card">
        <div className="pdv-card-header">
          <span className="pdv-card-title">Detalles De Parametros</span>
        </div>
        <div className="pdv-card-body">
          <p className="pdv-line">Lottery: {lottery} - {lotteryName}</p>
          {scopeLabel && <p className="pdv-line">{scopeLabel}</p>}
          <hr className="pdv-sep" />

          {error && <p className="pdv-error">{error}</p>}
          {loading && <p className="pdv-loading">Chargement...</p>}

          {!loading && !error && rows.map((row) => (
            <div key={row.productId} className="pdv-group">
              <p className="pdv-group-name">{row.name}</p>
              {row.hasConfig ? (
                <>
                  <table className="pdv-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Points Price</th>
                        <th>Online Limit</th>
                        <th>Local Limit</th>
                        <th>Sales Comission</th>
                        <th>Savings %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{row.name}</td>
                        <td>{fmt(row.pointsPrice)}</td>
                        <td>{fmt(row.onlineLimit)}</td>
                        <td>{fmt(row.localLimit)}</td>
                        <td>{row.salesCommission}</td>
                        <td>{row.savingsPct}</td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="pdv-key-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Won per Point</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.keys.map((k) => (
                        <tr key={k.keyId}>
                          <td>{k.key}</td>
                          <td>{k.wonPerPoint === null ? '—' : fmt(k.wonPerPoint)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="pdv-no-config">Aucune configuration pour ce produit sur cette loterie.</p>
              )}
            </div>
          ))}
        </div>
      </div>
  );

  if (embedded) return card;
  return (
    <Layout>
      <p className="pdv-back" onClick={onBack}>◀ Back to Search</p>
      {card}
    </Layout>
  );
};

export default ParameterDetailsView;
